import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { GAMES_API, type Games } from '@chess/api-games';
import { LOG_API, type Log } from '@chess/api-log';
import { TOAST_API, type Toast } from '@chess/api-toast';
import { SCAN_API, estimate, type ScanService } from '@chess/api-scan';
import { useScanProgress } from '@chess/api-scan/react';
import { SYNC_API, localDay, syncDue, type SyncService, type SyncState } from '@chess/api-sync';
import { useNavigate } from 'react-router';
import { reviewPath } from '@chess/api-games';
import { useStoreQuery } from '@chess/api-model/react';
import { STORE_API, type GameStore } from '@chess/api-model';
import { ARCHIVES_FOLDER, canImport, coolingMs, refreshDue, statusText, useImport, type Deps } from './store';
import { monthLabel } from './import.ts';
import { ImportButton } from './ImportButton.tsx';
import './library-import-chesscom.css';

// The chess.com importer: one button, "Sync", in the top bar of every screen
// on the rail, with its status line beside it. Each click fetches the months that
// hold the last 30 days and adds their games played in that window (older
// games are never imported), then asks the scan queue for a pass, so every
// game of the window not scanned yet is scanned, even when nothing new came
// in. The same button shows both: importing, then scanning. Nothing is
// enabled until Settings has a username.
//
// The same import is the daily sync (`sync`, `@chess/api-sync`), provided
// here over the import store: due while no import completed today (the
// local day, `lastSyncDay` in the bookkeeping) and none was skipped this
// session; run by the sync screen (plugin screen-sync) before any screen on
// the rail shows. A click on the button is a sync too, so a manual one
// counts for the day. `reset` starts the account over (the landing page's
// way in): the account's games go, and their scans with them, and so does
// the bookkeeping under `archives/chesscom/<account>/`.
export function setup(app: PiletApi) {
  const storage = app.getApi<Storage>(STORAGE_API);
  const settings = storage.then((s) => s.scope('settings'));
  const archives = storage.then((s) => s.scope(ARCHIVES_FOLDER));
  // Every chess.com call is logged with the reason the cache did not serve it,
  // through the app's log API rather than the console.
  const callLog = app.getApi<Log>(LOG_API).then((log) => log.child(app.meta.name));
  const deps: Promise<Deps> = Promise.all([app.getApi<Games>(GAMES_API), archives, callLog, app.getApi<Toast>(TOAST_API)]).then(
    ([games, s, log, toast]) => ({
      games,
      storage: s,
      log: (endpoint, reason) => log.debug(`GET ${endpoint}`, { endpoint, reason }),
      toast,
    }),
  );

  // The new-game toast (2026-10-14): after an import that brought in at least
  // one game, a toast with a Review button for the newest of them, "reading
  // it…" until the store holds it scanned (the queue takes it first), then
  // "ready" and gone after a while. Not on the sync screen (it offers the game
  // itself) nor on that game's review. The Review button navigates through
  // the control's router hook, kept here since the toast lives outside it.
  let navigateTo: ((path: string) => void) | undefined;
  const READY_TOAST_MS = 15_000;
  const announceNewGames = async (): Promise<void> => {
    const s = useImport.getState();
    const latest = s.latest;
    const count = s.progress?.imported ?? 0;
    if (s.status !== 'done' || !latest || count === 0) return;
    if (window.location.pathname === '/sync') return;
    const [toast, store] = await Promise.all([app.getApi<Toast>(TOAST_API), app.getApi<GameStore>(STORE_API)]);
    const summary = store.game(latest.id)?.summary;
    const path = summary ? reviewPath(summary) : undefined;
    if (!path || window.location.pathname === path) return;
    const who = count === 1 ? `New game vs ${latest.opponent}` : `${count} new games · the latest vs ${latest.opponent}`;
    const handle = toast.show(`${who} · reading it…`, { kind: 'info', sticky: true, action: { label: 'Review', onClick: () => navigateTo?.(path) } });
    let done = false;
    const settle = () => {
      if (done) return;
      done = true;
      unsubscribe();
      handle.update(`${who} · ready`);
      setTimeout(() => handle.close(), READY_TOAST_MS);
    };
    const unsubscribe = store.subscribe(() => {
      if (store.game(latest.id)?.facts?.scanned) settle();
    });
    if (store.game(latest.id)?.facts?.scanned) settle();
  };

  // The daily sync over the import store. `due` reads the bookkeeping again
  // (`load`), so a reload, or a change of account, is seen at once.
  let skippedDay: string | undefined;
  const sync: SyncService = {
    async due() {
      const [s, a] = await Promise.all([settings, archives]);
      await useImport.getState().load(s, a);
      const { username, lastSyncDay } = useImport.getState();
      return syncDue({ username, lastSyncDay, skippedDay }, new Date());
    },
    async run() {
      const d = await deps;
      await useImport.getState().importRecent(d, { auto: true });
      const scan = await app.getApi<ScanService>(SCAN_API);
      await scan.scanAll();
      void announceNewGames();
    },
    skip() {
      skippedDay = localDay(new Date());
    },
    async reset() {
      const [d, s, a] = await Promise.all([deps, settings, archives]);
      await useImport.getState().load(s, a);
      const { username } = useImport.getState();
      if (!username) return;
      (await app.getApi<ScanService>(SCAN_API)).stop();
      for (const game of await d.games.list({ owner: username })) await d.games.remove(game.id);
      await a.remove(username);
      skippedDay = undefined;
      useImport.setState({ status: 'idle', progress: undefined, error: undefined, note: undefined, imported: [], archives: undefined, lastSyncDay: undefined });
    },
    state(): SyncState {
      const s = useImport.getState();
      const phase = s.status === 'importing' ? 'importing' : s.status === 'done' ? 'done' : s.status === 'error' ? 'error' : 'idle';
      return {
        phase,
        first: s.lastSyncDay === undefined,
        month: s.status === 'importing' && s.progress ? monthLabel(`${s.progress.month}`) : undefined,
        imported: s.progress?.imported,
        skipped: s.progress?.skipped,
        note: s.note,
        error: s.error,
      };
    },
    subscribe: (listener) => useImport.subscribe(listener),
  };
  app.registerApi(SYNC_API, sync);

  // The refresh (2026-10-14): on load and whenever the tab comes back to the
  // foreground, if the last import is more than ten minutes old, chess.com
  // is read again, silently (no sync screen: that is the day's first sync),
  // and the scan pass follows, so a game played meanwhile comes in and is
  // scanned first (the queue is newest first). The daily sync is left to the
  // sync screen: nothing here runs while it is due.
  const refresh = async (): Promise<void> => {
    const [s, a] = await Promise.all([settings, archives]);
    await useImport.getState().load(s, a);
    if (await sync.due()) return;
    if (!refreshDue(useImport.getState(), new Date())) return;
    const d = await deps;
    await useImport.getState().importRecent(d, { auto: true });
    const scan = await app.getApi<ScanService>(SCAN_API);
    await scan.scanAll();
    void announceNewGames();
  };
  void refresh();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refresh();
  });

  const useLoaded = () => {
    const load = useImport((s) => s.load);
    React.useEffect(() => {
      Promise.all([settings, archives]).then(([s, a]) => load(s, a));
    }, []);
  };

  /** The importer's control, read from the import store and the scan. */
  const Control: React.FC = () => {
    useLoaded();
    const state = useImport();
    const navigate = useNavigate();
    navigateTo = navigate;
    // A clock for "Synced 3 min ago" and for the minute's wait after a click: every second while waiting, every half minute otherwise.
    const [now, setNow] = React.useState(() => new Date());
    const cooling = coolingMs(state, now);
    React.useEffect(() => {
      const id = setInterval(() => setNow(new Date()), cooling > 0 ? 1000 : 30_000);
      return () => clearInterval(id);
    }, [cooling > 0]);
    // The newest game of the last sync, once the store holds it scanned: the status links to its review.
    const latest = state.status === 'done' ? state.latest : undefined;
    const ready = useStoreQuery(app.getApi, (s) => (latest ? s.game(latest.id) : undefined), [latest?.id]);
    const readyLink = latest && ready?.facts?.scanned ? { label: `Your game vs ${latest.opponent} is ready ›`, href: reviewPath(ready.summary), onOpen: () => navigate(reviewPath(ready.summary)) } : undefined;
    const [scan, setScan] = React.useState<ScanService | undefined>(() => app.tryGetApi<ScanService>(SCAN_API));
    React.useEffect(() => {
      if (!scan) void app.getApi<ScanService>(SCAN_API).then(setScan);
    }, [scan]);
    const progress = useScanProgress(scan);
    const importing = state.status === 'importing';
    const scanning = !importing && !!progress?.running;
    const left = progress ? Math.max(0, progress.total - progress.done) : 0;
    return (
      <ImportButton
        phase={importing ? 'importing' : scanning ? 'scanning' : 'idle'}
        status={statusText(state, now)}
        error={state.status === 'error'}
        enabled={canImport(state)}
        cooling={cooling}
        ready={readyLink}
        imported={state.progress?.imported}
        scanned={progress?.done}
        total={progress?.total}
        estimate={scan && scanning ? estimate(left, scan.secondsPerGame()) : undefined}
        // The import's new games are scanned on their own (shared-scan); a
        // pass afterwards also scans games left unscanned (a failed scan,
        // nothing new to import).
        onImport={() =>
          void deps
            .then((d) => state.importRecent(d))
            .then(() => scan?.scanAll())
            .then(() => announceNewGames())
        }
      />
    );
  };
  // Every screen's top bar, on the right (`shared.headerActions`).
  app.registerExtension('shared.headerActions', Control, { order: 10 });
}
